/**
 * Project API key list page — `GET /projects/{projectId}/api-keys`.
 *
 * Follows the CoOrdio list-page pattern established by {@link ProjectListPage}: `ShellHeader` for
 * the title and primary action, `CordioAbpPagedDataTable` for the grid, one `DeleteDialog` driven by
 * `useDeleteDialog` (here for **revoke** — the dialog is not delete-only), and a BEM block named
 * after the page slug.
 *
 * ── COLUMN PLAN (field → header → width → cell type → filter) ──────────────────────────────────
 *   name        → Name       → 24% → text, sortable                              → toolbar search, popover text `name`
 *   keyPrefix   → Key Prefix → 18% → custom, monospace, prefix + mask            → toolbar search
 *   status      → Status     → 16% → custom Chip (Active / Revoked / Rotation …) → status tabs
 *   createdAt   → Created    → 14% → date, locale                                → none
 *   lastUsedAt  → Last Used  → 14% → date, locale                                → none
 *   expiresAt   → Expires    → 14% → date, locale                                → none
 *                                    (widths sum to 100%)
 *
 * DROPPED from the old MUI table: the `rotationDueAt` column. It was a seventh column whose only
 * job was to shout when rotation was near, and both of its signals are still on the page — the
 * status chip says "Rotation Due" / "Rotation Expired", and the banner above the grid counts the
 * keys due within 15 days. `scopes` was never a column and stays off the grid.
 *
 * WHY every filter, the search and the sort are applied in this file rather than by the API:
 * `apiKeyService.getApiKeys(projectId)` takes no query parameters at all — it returns the project's
 * whole key list. So the honest options were "no search, no filters, no paging" or "do it here on
 * the complete list". Because the list is complete in memory, the in-memory pass is exact: a
 * filtered page is not a filtered slice of a page. {@link selectApiKeyPage} is that pass, and it is
 * the *only* place any of it happens.
 *
 * SECURITY: a key's secret is shown exactly once, in the dialog that follows generation, and is
 * never fetched again — `ProjectApiKeyDto` carries only `keyPrefix`. Nothing below renders, logs,
 * copies into a tooltip or stores the full key; the reveal dialog and its "I've Copied the Key"
 * acknowledgement are preserved verbatim from the previous implementation.
 */

import { useCallback, useMemo, useState } from 'react';

import {
  Button,
  Checkbox,
  Chip,
  CoordioDropdown,
  CordioAbpPagedDataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ShellHeader,
  Textarea,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState, InlineErrorPanel } from '@coordio/ui/layout';
import { Copy, Eye, EyeOff, Plus, ShieldOff } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { apiKeyService } from '../services/apiKeyService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';

import type { CreateApiKeyRequest, GenerateApiKeyResponse, ProjectApiKeyDto } from '../types/apiKey';
import type { PagedResult } from '../types/tenant';

import './ApiKeyListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey` AND builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. The All tab therefore declares the
 * literal `'all'` — an empty value would emit `cordio-datatable-status-tab-` and the E2E grid
 * checklist (GRID-13) would select nothing. `'all'` is never treated as a filter value:
 * {@link selectApiKeyPage} maps it to "no filter".
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Revoked', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Maps to `isActive`. */
const STATUS_TAB_PARAM_KEY = 'status';

/** Rotation is "due soon" inside this window — the same threshold the banner copy quotes. */
const ROTATION_DUE_WINDOW_DAYS = 15;

/**
 * A row as the grid sees it.
 *
 * `status` and `rotationDueAt` are sent by the rotation-aware endpoint but are not on
 * `ProjectApiKeyDto` yet; declaring them here as optional replaces the old `(key as any)` casts
 * without pretending the DTO has caught up.
 */
type ApiKeyRow = ProjectApiKeyDto & {
  status?: string;
  rotationDueAt?: string;
};

function isRotationDueSoon(key: ApiKeyRow): boolean {
  if (!key.rotationDueAt) return false;
  const daysUntil = (new Date(key.rotationDueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return daysUntil <= ROTATION_DUE_WINDOW_DAYS && daysUntil > 0;
}

/** The status vocabulary the API itself uses, mapped to the design system's chip colours. */
function statusInfo(key: ApiKeyRow): { label: string; color: ChipColor } {
  if (key.status === 'ExpiredRotation') return { label: 'Rotation Expired', color: 'red' };
  if (key.status === 'PendingRotation' || isRotationDueSoon(key)) {
    return { label: 'Rotation Due', color: 'amber' };
  }
  if (!key.isActive) return { label: 'Revoked', color: 'red' };
  return { label: 'Active', color: 'green' };
}

function matchesSearch(key: ApiKeyRow, term: string): boolean {
  if (!term) return true;
  // The prefix is the public half of a key and is already on screen, so it is searchable; the
  // secret half is not held anywhere on this page.
  return `${key.name} ${key.keyPrefix}`.toLowerCase().includes(term);
}

/** Undefined sorts last in both directions: "never used" is not "used at the beginning of time". */
function compareRows(field: string, descending: boolean) {
  return (a: ApiKeyRow, b: ApiKeyRow): number => {
    // Cast through `unknown` because `ApiKeyRow` is a `type` alias whose keys are known and
    // strongly typed, and TS refuses the direct `Record<string, unknown>` widening. `field` comes
    // from the grid's column config, which is `keyof ApiKeyRow` by construction — this branch is
    // only reached for real fields.
    const left = (a as unknown as Record<string, unknown>)[field];
    const right = (b as unknown as Record<string, unknown>)[field];

    if (left === undefined || left === null) return right === undefined || right === null ? 0 : 1;
    if (right === undefined || right === null) return -1;

    // ISO timestamps compare chronologically as plain strings, so one comparator covers both the
    // name column and the three date columns.
    const result = String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });
    return descending ? -result : result;
  };
}

/**
 * Applies the grid's whole query — search, status tab, name filter, sort, page — to the complete
 * key list, and returns it in the `PagedResult` shape the grid adapter expects.
 *
 * Exported for the unit test: this function *is* the filtering contract of the page, because the
 * endpoint has none of its own.
 */
export function selectApiKeyPage(all: readonly ApiKeyRow[], query: CortexPagedQuery): PagedResult<ApiKeyRow> {
  const term = typeof query.searchTerm === 'string' ? query.searchTerm.trim().toLowerCase() : '';
  const nameFilter = typeof query.name === 'string' ? query.name.trim().toLowerCase() : '';
  const status = query[STATUS_TAB_PARAM_KEY];

  let rows = all.filter((key) => {
    if (!matchesSearch(key, term)) return false;
    if (nameFilter && !key.name.toLowerCase().includes(nameFilter)) return false;
    if (status === 'true' && !key.isActive) return false;
    if (status === 'false' && key.isActive) return false;
    return true;
  });

  if (query.sortBy) {
    rows = [...rows].sort(compareRows(query.sortBy, query.sortDescending === true));
  }

  const pageSize = query.pageSize > 0 ? query.pageSize : 25;
  const pageNumber = query.pageNumber > 0 ? query.pageNumber : 1;
  const start = (pageNumber - 1) * pageSize;
  const totalPages = Math.ceil(rows.length / pageSize);

  return {
    items: rows.slice(start, start + pageSize),
    totalCount: rows.length,
    pageNumber,
    pageSize,
    totalPages,
    hasPreviousPage: pageNumber > 1,
    hasNextPage: pageNumber < totalPages,
  };
}

export const ApiKeyListPage = () => {
  const currentProject = useContextStore((s) => s.currentProject);
  const availableProjects = useContextStore((s) => s.availableProjects);
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);
  /** Derived from the full list on every fetch, for the rotation banner above the grid. */
  const [rotationDueCount, setRotationDueCount] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState<CreateApiKeyRequest>({ name: '', expiresAt: undefined, scopes: [] });

  /** The full key, held only between generation and the user acknowledging the reveal dialog. */
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  const [reassignKey, setReassignKey] = useState<ApiKeyRow | null>(null);
  const [reassignTargetProject, setReassignTargetProject] = useState('');

  const { dialogRef, pendingItem, openDelete: openRevoke, closeDelete: closeRevoke } = useDeleteDialog<ApiKeyRow>();

  const projectId = currentProject?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<ApiKeyRow>(async (query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (!projectId) throw new Error('No project selected');

        const all = (await apiKeyService.getApiKeys(projectId)) as ApiKeyRow[];
        // The banner counts across the whole list, not the visible page, so it is derived here
        // where the whole list is in hand.
        setRotationDueCount(all.filter(isRotationDueSoon).length);
        return selectApiKeyPage(all, query);
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ApiKeyRow>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '24%', sortable: true },
      {
        field: 'keyPrefix',
        header: 'Key Prefix',
        type: 'custom',
        width: '18%',
        // The prefix is the identifying, public half of a key. The mask is what makes it obvious
        // that the rest is not retrievable.
        customCell: (row) => (
          <code className="api-key-list__prefix">
            {row.keyPrefix}
            <span className="api-key-list__prefix-mask" aria-label="remainder hidden">
              ••••
            </span>
          </code>
        ),
      },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '16%',
        customCell: (row) => {
          const { label, color } = statusInfo(row);
          return <Chip color={color} label={label} />;
        },
      },
      { field: 'createdAt', header: 'Created', type: 'date', dateFormat: 'locale', width: '14%', sortable: true },
      { field: 'lastUsedAt', header: 'Last Used', type: 'date', dateFormat: 'locale', width: '14%', sortable: true },
      { field: 'expiresAt', header: 'Expires', type: 'date', dateFormat: 'locale', width: '14%', sortable: true },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search keys by name or prefix' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'API key status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [{ key: 'name', label: 'Name', type: 'text', placeholder: 'Key name' }],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ApiKeyRow>>>(
    () => [
      {
        icon: 'arrow-right-left',
        tooltip: 'Reassign to another project',
        // A revoked key cannot be moved; `visible` rather than a disabled button, so the row does
        // not offer an action that does nothing.
        visible: (row) => row.isActive,
        onClick: (row) => {
          setReassignKey(row);
          setReassignTargetProject('');
        },
      },
      {
        icon: 'trash-2',
        tooltip: 'Revoke key',
        className: 'api-key-list__action--destructive',
        visible: (row) => row.isActive,
        onClick: (row) => openRevoke(row),
      },
    ],
    [openRevoke],
  );

  const handleGenerate = useCallback(async () => {
    if (!projectId || !formData.name) return;

    try {
      const response: GenerateApiKeyResponse = await apiKeyService.generateApiKey(projectId, formData);
      // Straight into the reveal dialog: this is the only moment the full key exists in the client.
      setGeneratedKey(response.fullKey);
      setShowKey(false);
      setCreateOpen(false);
      setListVersion((v) => v + 1);
      showNotification('API key generated successfully', 'success');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to generate API key', 'error');
    }
  }, [projectId, formData, showNotification]);

  const handleCopyKey = useCallback(async () => {
    if (!generatedKey) return;
    try {
      await navigator.clipboard.writeText(generatedKey);
      showNotification('API key copied to clipboard', 'success');
    } catch {
      showNotification('Failed to copy to clipboard', 'error');
    }
  }, [generatedKey, showNotification]);

  /** Acknowledging the reveal drops the only copy of the secret the client ever holds. */
  const handleAcknowledgeKey = useCallback(() => {
    setGeneratedKey(null);
    setShowKey(false);
  }, []);

  const handleRevokeConfirm = useCallback(async () => {
    if (!projectId || !pendingItem) return;

    await apiKeyService.revokeApiKey(projectId, pendingItem.id);
    showNotification('API key revoked successfully', 'success');
    setListVersion((v) => v + 1);
  }, [projectId, pendingItem, showNotification]);

  const handleReassign = useCallback(async () => {
    if (!projectId || !reassignKey || !reassignTargetProject) return;

    try {
      await apiKeyService.reassignApiKey(projectId, reassignKey.id, reassignTargetProject);
      showNotification('API key reassigned successfully', 'success');
      setReassignKey(null);
      setReassignTargetProject('');
      setListVersion((v) => v + 1);
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to reassign API key', 'error');
    }
  }, [projectId, reassignKey, reassignTargetProject, showNotification]);

  if (!projectId) {
    return (
      <div className="api-key-list" data-testid="api-key-list-page">
        <ShellHeader
          title="API Keys"
          description="Project API keys authenticate the public agent, audio and function endpoints."
          data-testid="api-key-list-header"
        />
        <EmptyState
          title="No project selected"
          description="Choose a project from the switcher above to see the API keys issued for it."
          data-testid="api-key-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="api-key-list" data-testid="api-key-list-page">
      <ShellHeader
        title="API Keys"
        description={`Keys issued for ${currentProject.name}. The full key is shown once, at creation, and cannot be retrieved afterwards.`}
        data-testid="api-key-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-api-key-btn"
            onClick={() => {
              setFormData({ name: '', expiresAt: undefined, scopes: [] });
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Generate API Key
          </Button>
        }
      />

      {rotationDueCount > 0 && (
        <InlineErrorPanel
          className="api-key-list__rotation-banner"
          data-testid="api-key-rotation-banner"
          message={`${rotationDueCount} API key${rotationDueCount > 1 ? 's are' : ' is'} due for rotation within ${ROTATION_DUE_WINDOW_DAYS} days. Rotate ${rotationDueCount > 1 ? 'them' : 'it'} to maintain security.`}
        />
      )}

      <div data-testid="api-key-list-grid">
        <CordioAbpPagedDataTable<ApiKeyRow>
          className="api-key-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
        />
      </div>

      {/* Revoke, through the shared confirmation dialog — which is not delete-only: it takes its
          own title, icon, confirm label and busy label. */}
      <DeleteDialog
        ref={dialogRef}
        title="Revoke API key?"
        entityLabel={pendingItem ? `${pendingItem.name} (${pendingItem.keyPrefix}…)` : undefined}
        dialogIcon={<ShieldOff className="delete-dialog__icon" />}
        description="Every integration using this key stops working immediately. A revoked key cannot be restored — issue a new one instead. This cannot be undone."
        confirmLabel="Revoke"
        confirmVariant="destructive"
        busyLabel="Revoking…"
        onConfirm={handleRevokeConfirm}
        onCancel={closeRevoke}
      />

      {/* ── Generate ────────────────────────────────────────────────────────── */}
      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) setCreateOpen(false); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generate API Key</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-1">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Name <span className="text-red-500">*</span></label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="api-key-name-input"
              />
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">A descriptive name for this API key</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Expiration Date</label>
              <Input
                type="datetime-local"
                value={formData.expiresAt || ''}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value || undefined })}
                data-testid="api-key-expiration-input"
              />
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">Optional: Leave empty for no expiration</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Scopes</label>
              <div className="flex flex-wrap gap-3 mt-1">
                {[
                  { label: 'Read', value: 'read' },
                  { label: 'Write', value: 'write' },
                  { label: 'Admin', value: 'admin' },
                ].map((scope) => (
                  <label key={scope.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox
                      checked={formData.scopes.includes(scope.value)}
                      onCheckedChange={(checked) => {
                        const next = checked
                          ? [...formData.scopes, scope.value]
                          : formData.scopes.filter((s) => s !== scope.value);
                        setFormData({ ...formData, scopes: next });
                      }}
                      data-testid={`scope-${scope.value}`}
                    />
                    {scope.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              You will only be able to view the full API key once. Make sure to copy it before closing this dialog.
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} data-testid="cancel-api-key-btn">Cancel</Button>
            <Button type="button" variant="default" onClick={() => void handleGenerate()} disabled={!formData.name} data-testid="generate-api-key-btn">
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reassign ────────────────────────────────────────────────────────── */}
      <Dialog open={reassignKey !== null} onOpenChange={(open) => { if (!open) setReassignKey(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reassign API Key</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-1">
            <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
              The API key will be moved to the selected project. It will no longer be accessible from the current
              project.
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Target Project</label>
              <CoordioDropdown
                value={reassignTargetProject || null}
                onChange={(v) => setReassignTargetProject(v ?? '')}
                options={availableProjects
                  .filter((project) => project.id !== projectId)
                  .map((project) => ({ label: project.name, value: project.id }))}
                data-testid="reassign-target-project"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReassignKey(null)} data-testid="cancel-reassign-btn">Cancel</Button>
            <Button type="button" variant="default" onClick={() => void handleReassign()} disabled={!reassignTargetProject} data-testid="confirm-reassign-btn">
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reveal, once ────────────────────────────────────────────────────────
          Preserved exactly: the secret is masked by default, can be revealed and copied, and the
          only way out is the acknowledgement — which clears it from state for good. */}
      <Dialog open={generatedKey !== null} onOpenChange={(open) => { if (!open) handleAcknowledgeKey(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>API Key Generated</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
            This is the only time you will be able to view the full API key. Copy it now and store it securely.
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">API Key</label>
            <div className="relative">
              <Textarea
                className="w-full font-mono text-sm"
                value={generatedKey || ''}
                readOnly
                rows={3}
                style={{ WebkitTextSecurity: showKey ? 'none' : 'disc' } as React.CSSProperties}
                data-testid="reveal-api-key-textarea"
              />
              <div className="absolute right-2 top-2 flex gap-1">
                <Button type="button" variant="ghost" size="icon" onClick={() => setShowKey(!showKey)} aria-label="Toggle key visibility" data-testid="toggle-key-visibility-btn">
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => void handleCopyKey()} aria-label="Copy key" data-testid="copy-key-btn">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="default" onClick={handleAcknowledgeKey} data-testid="acknowledge-key-btn">
              I&apos;ve Copied the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
